/**
 * @graphql cc
 */
import { useState, useContext, useEffect } from 'react';
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';

import { useSessionData } from '@confluence/session-data';
import {
	useIsLivePagesFeatureEnabled,
	LIVE_DOC_GA_EXP_TRAIT_NAME,
} from '@confluence/live-pages-utils/entry-points/useIsLivePagesFeatureEnabled';
import { SPAViewContext } from '@confluence/spa-view-context';
import { confluenceLocalStorageInstance as localStorage, keys } from '@confluence/storage-manager';

import type {
	InitModernizationGaMutation as InitModernizationGaMutationType,
	InitModernizationGaMutationVariables,
} from './__types__/InitModernizationGaMutation';

const InitModernizationGaMutation = gql`
	mutation InitModernizationGaMutation($cloudId: ID!) {
		confluence_experimentInitModernize(cloudId: $cloudId) {
			success
		}
	}
`;

/**
 * This component is used to initialize the Modernization GA experiment.
 * It does not render anything.
 */
export const InitModernizationGa = () => {
	const [isInitialized, setIsInitialized] = useState(false);

	const { cloudId } = useSessionData();
	const [initModernizationGaMutation] = useMutation<
		InitModernizationGaMutationType,
		InitModernizationGaMutationVariables
	>(InitModernizationGaMutation, {
		variables: { cloudId },
	});

	const hasInitializedModernizationGa = localStorage.getItem(keys.LIVE_DOCS_HAS_INIT_MODERNIZE);
	const { isLivePagesFeatureEnabled } = useIsLivePagesFeatureEnabled();
	const { experimentFeatures, loading: isSPAViewContextLoading } = useContext(SPAViewContext);

	useEffect(() => {
		if (
			isInitialized ||
			hasInitializedModernizationGa ||
			isLivePagesFeatureEnabled ||
			isSPAViewContextLoading ||
			!experimentFeatures
		) {
			return;
		}
		try {
			const modernizationGaTraitValue =
				JSON.parse(experimentFeatures)?.[LIVE_DOC_GA_EXP_TRAIT_NAME];
			if (modernizationGaTraitValue === 'null' || !modernizationGaTraitValue) {
				setIsInitialized(true);
				void initModernizationGaMutation().then(({ data }) => {
					data?.confluence_experimentInitModernize?.success &&
						localStorage.setItem(keys.LIVE_DOCS_HAS_INIT_MODERNIZE, 'true');
				});
			}
		} catch (e) {
			// do nothing
		}
	}, [
		isInitialized,
		hasInitializedModernizationGa,
		isLivePagesFeatureEnabled,
		isSPAViewContextLoading,
		experimentFeatures,
		initModernizationGaMutation,
	]);

	return null;
};
